import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { SearchSelect } from "../components/SearchSelect";
import {
  Plus, Trash2, Sprout, FileDown, Camera, Image as ImageIcon, X,
  FileArchive, MapPin, ShieldCheck, ShieldAlert, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { todayIST, nowISOIST } from "../lib/datefmt";
import { LazyPhoto } from "../components/LazyPhoto";
import { compressImage } from "../lib/image";

const TEAM_LEAD_ROLES = ["admin", "director", "rm_and_above", "manager", "sales_manager"];
const DISTANCE_WARN_M = 1000; // >1 km from registered farmer → flag as suspicious

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: true });
  } catch {
    return iso;
  }
}

function DistanceBadge({ meters }) {
  if (meters == null) {
    return <span className="text-[11px] text-[#8A8577]">no farmer GPS</span>;
  }
  const km = meters / 1000;
  const warn = meters > DISTANCE_WARN_M;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded ${
        warn ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"
      }`}
      title={warn ? `Photo taken ${km.toFixed(2)} km away from farmer's registered location` : "Photo location matches farmer"}
    >
      {warn ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
      {km < 1 ? `${Math.round(meters)} m` : `${km.toFixed(2)} km`}
    </span>
  );
}

export default function DemonstrationsPage() {
  const { user } = useAuth();
  const canVerify = TEAM_LEAD_ROLES.includes(user?.role);
  const canDownloadZip = TEAM_LEAD_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const initialForm = {
    farmer_id: "",
    product_id: "",
    crop: "",
    remarks: "",
    demo_date: todayIST(),
    photo_b64: "",
    photo_lat: null,
    photo_lng: null,
    photo_accuracy_m: null,
    photo_captured_at: null,
  };
  const [form, setForm] = useState(initialForm);

  const load = async () => {
    const [d, f, p] = await Promise.all([
      api.get("/demonstrations"),
      api.get("/farmers"),
      api.get("/products"),
    ]);
    setRows(d.data || []);
    setFarmers(f.data || []);
    setProducts(p.data || []);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const captureGps = () =>
    new Promise((resolve) => {
      if (!("geolocation" in navigator)) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });

  const onPhoto = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) return toast.error("Photo too large (max 12MB)");

    setGpsBusy(true);
    const gpsPromise = captureGps();
    const readerPromise = compressImage(file);

    try {
      const [dataUrl, gps] = await Promise.all([readerPromise, gpsPromise]);
      if (!gps) toast.warning("Could not read GPS location — please enable location and retry, or the demo will be saved without coordinates");
      setForm((f) => ({
        ...f,
        photo_b64: dataUrl,
        photo_lat: gps?.lat ?? null,
        photo_lng: gps?.lng ?? null,
        photo_accuracy_m: gps?.accuracy ?? null,
        photo_captured_at: nowISOIST(),
      }));
    } catch {
      toast.error("Could not read photo");
    } finally {
      setGpsBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.farmer_id || !form.product_id) return toast.error("Farmer and product are required");
    if (!form.photo_b64) return toast.error("Please attach a farmer/field photo for verification");
    try {
      await api.post("/demonstrations", form);
      toast.success("Demonstration logged");
      setOpen(false);
      setForm(initialForm);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this demonstration?")) return;
    try {
      await api.delete(`/demonstrations/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const verify = async (row, verified) => {
    const remarks = verified
      ? (window.prompt("Optional verification remarks (leave blank to skip):") || "")
      : "";
    try {
      await api.post(`/demonstrations/${row.id}/verify`, { verified, remarks });
      toast.success(verified ? "Marked as verified" : "Verification cleared");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get("/reports/demonstrations.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "demonstrations_report.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const downloadPhotosZip = async () => {
    try {
      const res = await api.get("/reports/demonstration-photos.zip", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "demonstration_photos.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div>
      <PageHeader
        title="Demonstrations"
        subtitle="Product demos delivered to farmers"
        actions={
          <div className="flex gap-2 flex-wrap">
            {canDownloadZip && (
              <Button variant="outline" onClick={downloadPhotosZip} data-testid="download-demo-photos-zip">
                <FileArchive className="w-4 h-4" /> Photos ZIP
              </Button>
            )}
            <Button variant="outline" onClick={downloadReport} data-testid="download-demos">
              <FileDown className="w-4 h-4" /> Excel
            </Button>
            <Button onClick={() => setOpen((s) => !s)} data-testid="new-demo-btn">
              <Plus className="w-4 h-4" /> New demonstration
            </Button>
          </div>
        }
      />

      {open && (
        <Card className="mb-6">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Farmer</Label>
              <SearchSelect
                value={form.farmer_id}
                onChange={(v) => setForm({ ...form, farmer_id: v })}
                required
                testId="demo-farmer"
                placeholder="Search farmer by name or village…"
                options={farmers.map((f) => ({
                  value: f.id,
                  label: f.name,
                  sublabel: f.village || undefined,
                  keywords: [f.code, f.mobile].filter(Boolean),
                }))}
              />
            </div>
            <div>
              <Label>Product</Label>
              <SearchSelect
                value={form.product_id}
                onChange={(v) => setForm({ ...form, product_id: v })}
                required
                testId="demo-product"
                placeholder="Search product…"
                options={products.map((p) => ({
                  value: p.id,
                  label: p.name,
                  sublabel: p.code,
                }))}
              />
            </div>
            <div>
              <Label>Crop</Label>
              <Input value={form.crop} onChange={(e) => setForm({ ...form, crop: e.target.value })} placeholder="e.g. Wheat, Paddy" data-testid="demo-crop" />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.demo_date} onChange={(e) => setForm({ ...form, demo_date: e.target.value })} required data-testid="demo-date" />
            </div>
            <div className="sm:col-span-2">
              <Label>Remarks</Label>
              <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Notes on the demo, farmer response, next steps" data-testid="demo-remarks" />
            </div>
            <div className="sm:col-span-2">
              <Label>
                Farmer / field photo <span className="text-red-600">*</span>
                <span className="text-xs text-[#5C6B5E] ml-1">(required — GPS is captured with the photo for manager verification)</span>
              </Label>
              <label className={`inline-flex items-center gap-2 cursor-pointer rounded-lg px-4 py-2 font-semibold text-sm ${gpsBusy ? "bg-gray-200 text-gray-500" : "bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20"}`}>
                {gpsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {gpsBusy ? "Reading GPS…" : (form.photo_b64 ? "Replace photo" : "Attach photo")}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  data-testid="demo-photo-input"
                  disabled={gpsBusy}
                  onChange={(e) => onPhoto(e.target.files?.[0])}
                />
              </label>
              {form.photo_b64 && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={form.photo_b64} alt="Demo" className="h-24 w-24 rounded object-cover border" data-testid="demo-photo-preview" />
                  <div className="text-xs text-[#5C6B5E]" data-testid="demo-photo-gps">
                    {form.photo_lat != null ? (
                      <>
                        <div className="flex items-center gap-1 text-[#1E3F20] font-semibold">
                          <MapPin className="w-3.5 h-3.5" />
                          {form.photo_lat.toFixed(6)}, {form.photo_lng.toFixed(6)}
                        </div>
                        <div>Accuracy ±{Math.round(form.photo_accuracy_m || 0)} m</div>
                      </>
                    ) : (
                      <div className="text-amber-700">No GPS recorded — enable location & retry</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, photo_b64: "", photo_lat: null, photo_lng: null, photo_accuracy_m: null, photo_captured_at: null })}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="demo-submit" disabled={gpsBusy}>Save</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState icon={Sprout} title="No demonstrations yet" description="Log your first product demo above." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Photo</th>
                  <th className="px-4 py-3">Farmer</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Sales Person</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Verification</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E2DC] align-top" data-testid={`demo-row-${r.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{r.demo_date}</td>
                    <td className="px-4 py-3">
                      {r.has_photo || r.photo_b64 ? (
                        <LazyPhoto
                          kind="demonstration"
                          id={r.id}
                          alt="Demo"
                          title={`${r.farmer_name} — ${r.product_name}`}
                          className="h-12 w-12 rounded border hover:opacity-80"
                          onOpen={(url, title) => setPreview({ url, title, row: r })}
                          testid={`demo-photo-thumb-${r.id}`}
                        />
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-[#8A8577]">
                          <ImageIcon className="w-3.5 h-3.5" /> —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.farmer_name}</div>
                      <div className="text-xs text-[#5C6B5E]">{r.farmer_village}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{r.product_name}</div>
                      <div className="text-xs text-[#5C6B5E]">{r.crop || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-[#5C6B5E]">{r.user_name}</td>
                    <td className="px-4 py-3">
                      {r.photo_lat != null ? (
                        <div className="flex flex-col gap-1">
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${r.photo_lat},${r.photo_lng}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#1E3F20] hover:underline"
                            data-testid={`demo-map-link-${r.id}`}
                          >
                            <MapPin className="w-3 h-3" />
                            {r.photo_lat.toFixed(4)}, {r.photo_lng.toFixed(4)}
                          </a>
                          <DistanceBadge meters={r.distance_from_farmer_m} />
                        </div>
                      ) : (
                        <span className="text-xs text-[#8A8577]">No GPS</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.verified_at ? (
                        <div className="text-xs" data-testid={`demo-verified-${r.id}`}>
                          <div className="inline-flex items-center gap-1 text-emerald-800 font-semibold">
                            <ShieldCheck className="w-3.5 h-3.5" /> Verified
                          </div>
                          <div className="text-[#5C6B5E]">by {r.verified_by_name}</div>
                          <div className="text-[#5C6B5E]">{fmtDate(r.verified_at)}</div>
                          {r.verify_remarks && <div className="text-[#5C6B5E] italic">"{r.verify_remarks}"</div>}
                        </div>
                      ) : (
                        <span className="text-xs text-[#8A8577]">Pending</span>
                      )}
                      {canVerify && (
                        r.verified_at ? (
                          <button
                            type="button"
                            onClick={() => verify(r, false)}
                            className="text-[11px] text-red-600 hover:underline mt-1"
                            data-testid={`demo-unverify-${r.id}`}
                          >
                            Unverify
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => verify(r, true)}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 rounded px-2 py-0.5"
                            data-testid={`demo-verify-${r.id}`}
                          >
                            <ShieldCheck className="w-3 h-3" /> Verify
                          </button>
                        )
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => del(r.id)}
                        className="text-red-600 p-1 hover:bg-red-50 rounded"
                        data-testid={`del-demo-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="demo-photo-modal"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-200"
              data-testid="demo-photo-modal-close"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="text-white mb-2 text-sm font-semibold flex items-center justify-between gap-4">
              <span>{preview.title}</span>
              {preview.row?.photo_lat != null && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${preview.row.photo_lat},${preview.row.photo_lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 rounded px-2 py-1 text-xs"
                >
                  <MapPin className="w-3.5 h-3.5" /> Open in Maps
                </a>
              )}
            </div>
            <img src={preview.url} alt="Demo full" className="w-full max-h-[80vh] object-contain rounded-lg bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
